import React, { useEffect } from "react"
import { createPortal } from "react-dom"
import "./LearnerModal.style.scss"

export interface LearnerModalProps {
  isOpen: boolean
  title: string
  description?: string
  /** Extra class on the dialog panel (e.g. form layouts). */
  dialogClassName?: string
  isBusy?: boolean
  onClose: () => void
  children?: React.ReactNode
  footer?: React.ReactNode
}

/**
 * Shared learner modal shell (portal, escape, overlay click, scroll lock).
 * Compose confirm dialogs and form modals on top of this.
 */
const LearnerModal = ({
  isOpen,
  title,
  description,
  dialogClassName = "",
  isBusy = false,
  onClose,
  children,
  footer,
}: LearnerModalProps) => {
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isBusy) onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = prev
    }
  }, [isOpen, isBusy, onClose])

  if (!isOpen) return null

  const titleId = "learner-modal-title"

  return createPortal(
    <div
      className="learner-modal"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isBusy) onClose()
      }}
    >
      <div
        className={`learner-modal__dialog${dialogClassName ? ` ${dialogClassName}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="learner-modal__header">
          <h2 id={titleId}>{title}</h2>
          {description ? <p>{description}</p> : null}
        </header>
        {children ? (
          <div className="learner-modal__body">{children}</div>
        ) : null}
        {footer ? (
          <footer className="learner-modal__footer">{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body
  )
}

export default LearnerModal
